<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

// OCHAT: confirmación antes de borrar una campaña del historial.
//
// Antes "Eliminar" borraba al instante y solo de Ochat. Un cliente lo usó
// creyendo que también lo sacaba de Instagram, y el posteo seguía publicado. Por
// eso pregunta, ofrece borrarlo también de las redes, y dice antes qué se va a
// poder borrar y qué hay que borrar a mano.
const props = defineProps({
  campaign: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();

const dialogRef = ref(null);
const removeFromNetworks = ref(true);

// Solo lo que llegó a publicarse tiene algo que borrar en la red.
const publishedTargets = computed(() =>
  (props.campaign?.post?.targets ?? []).filter(
    target => target.remote_deletion !== 'nothing'
  )
);

const targetLabel = target =>
  `${target.inbox_name} · ${
    target.platform === 'instagram'
      ? t('MARKETING.CAMPAIGN.PREVIEW.INSTAGRAM')
      : t('MARKETING.CAMPAIGN.PREVIEW.FACEBOOK')
  }`;

const OUTCOME_CLASS = {
  supported: 'text-n-teal-11',
  needs_permission: 'text-n-amber-11',
  manual: 'text-n-amber-11',
};

const outcomeText = target =>
  ({
    supported: t('MARKETING.HISTORY.DELETE_DIALOG.WILL_DELETE'),
    needs_permission: t('MARKETING.HISTORY.DELETE_DIALOG.NEEDS_PERMISSION'),
    manual: t('MARKETING.HISTORY.DELETE_DIALOG.MANUAL'),
  })[target.remote_deletion];

const open = () => {
  removeFromNetworks.value = true;
  dialogRef.value.open();
};

const close = () => dialogRef.value.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="xl"
    :title="t('MARKETING.HISTORY.DELETE_DIALOG.TITLE')"
    :confirm-button-label="t('MARKETING.HISTORY.DELETE')"
    :is-loading="isLoading"
    @confirm="emit('confirm', { removeFromNetworks })"
  >
    <div class="flex flex-col gap-4 text-sm">
      <template v-if="publishedTargets.length">
        <label class="flex items-start gap-2 cursor-pointer">
          <Checkbox v-model="removeFromNetworks" />
          <span class="text-n-slate-12">
            {{ t('MARKETING.HISTORY.DELETE_DIALOG.REMOVE_FROM_NETWORKS') }}
          </span>
        </label>

        <ul v-if="removeFromNetworks" class="flex flex-col gap-1.5">
          <li
            v-for="target in publishedTargets"
            :key="target.id"
            class="flex flex-col"
          >
            <span class="text-n-slate-12">{{ targetLabel(target) }}</span>
            <span
              class="text-xs"
              :class="OUTCOME_CLASS[target.remote_deletion]"
            >
              {{ outcomeText(target) }}
            </span>
          </li>
        </ul>
        <p v-else class="text-xs text-n-amber-11">
          {{ t('MARKETING.HISTORY.DELETE_DIALOG.ONLY_OCHAT') }}
        </p>
      </template>

      <p v-if="campaign?.broadcast" class="text-xs text-n-slate-11">
        {{ t('MARKETING.HISTORY.DELETE_DIALOG.WHATSAPP_SENT') }}
      </p>
    </div>
  </Dialog>
</template>
